import { classNames } from "helpers/ClassNameHelper";
import * as React from "react";

interface PtClassName {
    container: string[];
    field: string[];
    header: string[];
    title: string[];
    fieldValue: string[];
    fieldError: string[];
    inlineRemarks: string[];
}

interface Props {
    id?: string;
    name?: string;
    errorMessage?: string;
    required?: boolean;
    horizontal?: boolean;
    inlineRemarks?: (React.ReactElement | string | number)[];
    spanFieldColumn?: 1 | 2 | 3;
    fieldColumns?: 1 | 2 | 3;
    gap?: 4 | 8 | 12 | 16 | 20 | 24 | 28 | 32 | 36;
    ptClassName?: Partial<PtClassName>;
}

const defaultPtClassName: PtClassName = {
    container: [],
    field: [],
    header: [],
    title: [],
    fieldValue: [],
    fieldError: [],
    inlineRemarks: [],
};

export const Field = ({
    children,
    id,
    name,
    errorMessage,
    required = false,
    horizontal = false,
    inlineRemarks = [],
    spanFieldColumn,
    fieldColumns,
    gap,
    ptClassName = {},
}: React.PropsWithChildren<Props>) => {
    const childrenCount = React.Children.count(children);
    const classes = { ...defaultPtClassName, ...ptClassName };

    const displayErrorMsg = () => (
        <div
            style={{
                textAlign: children ? "right" : "left",
            }}
            className={classNames("field-error", ...classes.fieldError)}
        >
            <small>{errorMessage}</small>
        </div>
    );

    return (
        <div className={classNames(`field-container`, ...classes.container)}>
            <div
                id={id}
                style={
                    horizontal
                        ? {
                              gridTemplateColumns: `repeat(${
                                  (fieldColumns ?? (childrenCount || 1)) + 1
                              }, 1fr)`,
                          }
                        : {
                              gridTemplateRows: "initial",
                          }
                }
                className={classNames(
                    `field`,
                    gap ?? horizontal ? "g-24" : "g-8",
                    ...classes.field
                )}
            >
                <div
                    style={{
                        gridTemplateColumns: `repeat(${inlineRemarks.length + 1}, 1fr)${
                            errorMessage ? " auto" : ""
                        }`,
                    }}
                    className={classNames(
                        "field-header",
                        gap ?? horizontal ? "g-24" : "g-8",
                        ...classes.header
                    )}
                >
                    <span
                        className={classNames(
                            "field-title",
                            !!errorMessage && "field-error",
                            ...classes.title
                        )}
                    >
                        {required ? `${name} *` : name}
                    </span>
                    {inlineRemarks &&
                        inlineRemarks.map((element, i) => (
                            <span
                                key={i}
                                className={classNames(
                                    "inline-field-remark",
                                    ...classes.inlineRemarks
                                )}
                            >
                                {element}
                            </span>
                        ))}
                    {!horizontal && errorMessage && displayErrorMsg()}
                </div>
                {!children && errorMessage && displayErrorMsg()}
                {children && (
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${childrenCount}, 1fr)`,
                            gridColumn: spanFieldColumn && `span ${spanFieldColumn}`,
                        }}
                        className={classNames(
                            "field-value",
                            gap ?? horizontal ? "g-24" : "g-8",
                            ...classes.fieldValue
                        )}
                    >
                        {children}
                    </div>
                )}
            </div>
            {Boolean(children) && horizontal && errorMessage && displayErrorMsg()}
        </div>
    );
};
